import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgModule,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Params, RouterModule } from '@angular/router';

import { appendQueryParamsToPath } from '@app/utils/link.utils';
import { TrackLinkFunction, TrackLinkModule } from '@app/utils/track-link.directive';

export enum OmguiButtonVariant {
  primary = 'primary',
  secondary = 'secondary',
  link = 'link',
  yellowPrimary = 'yellow-primary',
}

export enum OmguiButtonSize {
  small = 'sm',
  large = 'lg',
  fillParent = 'fill-parent',
}

export class ConflictingInputError extends Error {
  name = 'ConflictingInputError';
  constructor(klass: Function, inputNames: string[]) {
    super(`Conflicting Inputs ${inputNames.join(', ')} on ${klass.name}`);
    // Makes Typescript + Jest happy, see https://github.com/facebook/jest/issues/8279#issuecomment-539775425
    Object.setPrototypeOf(this, ConflictingInputError.prototype);
  }
}

export enum OmguiButtonType {
  button = 'button',
  submit = 'submit',
  reset = 'reset',
}

@Component({
  selector: 'omgui-button',
  templateUrl: './omgui-button.component.html',
  styleUrls: ['./omgui-button.component.scss'],
})
export class OmguiButtonComponent implements OnInit, OnChanges, OnDestroy {
  @Input() variant: OmguiButtonVariant = OmguiButtonVariant.primary;
  @Input() size: OmguiButtonSize = OmguiButtonSize.large;
  @Input() disabled = false;
  @Input() newTab = false;
  @Input() externalLink: string;
  @Input() internalLink: string;
  @Input() semanticElementLink = false;
  @Input() trackLink: TrackLinkFunction;
  @Input() type: OmguiButtonType = OmguiButtonType.submit;
  @Input() queryParams: Params = {};
  @Input() customButtonClass: Record<string, boolean> | string;
  @Input() nativeAppCallback: () => void;
  @Input() maxWaitTime: number;

  @Output() maxWaitExceeded = new EventEmitter<void>();
  @Output() buttonClick = new EventEmitter();

  @ViewChild('buttonRef') buttonRef: ElementRef;

  buttonClass: Record<string, boolean>;

  protected emitButtonClick: boolean;
  protected role: string;

  private _timeoutRef: number;

  /**
   * Sets the focus on the button.
   */
  focus(): void {
    this.buttonRef.nativeElement.focus();
  }

  /**
   * @ignore
   */
  ngOnInit(): void {
    if (this.externalLink && this.internalLink) {
      throw new ConflictingInputError(OmguiButtonComponent, ['externalLink', 'internalLink']);
    }

    this.buttonClass = this.getButtonClass();
    this.externalLink = appendQueryParamsToPath(this.externalLink, this.queryParams);
    this.emitButtonClick = !this.externalLink && !this.internalLink;
    this.role = this.getRole();
  }

  /**
   * @ignore
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.variant || changes.size || changes.customButtonClass) {
      this.buttonClass = this.getButtonClass();
    }

    if (changes.externalLink || changes.queryParams) {
      this.externalLink = appendQueryParamsToPath(this.externalLink, this.queryParams);
    }
  }

  ngOnDestroy() {
    clearTimeout(this._timeoutRef);
  }

  protected handler(event: Event): void {
    if (this.maxWaitTime > 0) {
      this._timeoutRef = window.setTimeout(() => {
        this.maxWaitExceeded.emit();
      }, this.maxWaitTime * 1000);
    }

    if (this.emitButtonClick) {
      this.buttonClick.emit(event);
      return;
    }

    if (!this.nativeAppCallback) {
      return;
    }

    event.preventDefault();
    this.nativeAppCallback();
  }

  /**
   * @ignore
   */
  private getButtonClass(): Record<string, boolean> {
    const buttonClass: Record<string, boolean> = {
      'rounded-pill': this.variant !== OmguiButtonVariant.link,
      'btn-primary': this.variant === OmguiButtonVariant.primary,
      'btn-outline-primary': this.variant === OmguiButtonVariant.secondary,
      'btn-yellow-primary': this.variant === OmguiButtonVariant.yellowPrimary,
      'btn-link': this.variant === OmguiButtonVariant.link,
      'btn-sm': this.size === OmguiButtonSize.small,
      'w-100': this.size === OmguiButtonSize.fillParent,
      btn: true,
    };

    if (typeof this.customButtonClass === 'string') {
      buttonClass[this.customButtonClass] = true;
    }

    if (typeof this.customButtonClass === 'object') {
      Object.assign(buttonClass, buttonClass, this.customButtonClass);
    }

    return buttonClass;
  }

  /**
   * @ignore
   */
  private getRole(): string {
    const role = this.variant === OmguiButtonVariant.link ? 'link' : 'button';
    return role;
  }
}

@NgModule({
  declarations: [OmguiButtonComponent],
  imports: [CommonModule, RouterModule, TrackLinkModule],
  exports: [OmguiButtonComponent],
})
export class OmguiButtonModule {}
